import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Search, CalendarDays, Clock, ArrowRight, Mail } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { INSIGHT_CATEGORIES } from "@/data/site";
import { ARTICLES } from "@/data/articles";
import { API_URL } from "@/config/company";

const newsletterSchema = z.object({ email: z.string().email("Enter a valid work email address") });

function NewsletterForm() {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(newsletterSchema) });
  const onSubmit = async (data) => {
    try {
      const res = await fetch(`${API_URL}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error();
      toast.success("Subscribed — we will notify you when new insights are published.");
      reset();
    } catch {
      toast.error("Subscription could not be saved. Please try again later.");
    }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col sm:flex-row gap-3 max-w-xl" noValidate data-testid="newsletter-form">
      <div className="flex-1">
        <label htmlFor="newsletter-email" className="sr-only">Work email address</label>
        <input
          id="newsletter-email"
          type="email"
          placeholder="Your work email"
          data-testid="newsletter-email-input"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? "newsletter-email-error" : undefined}
          className="w-full rounded-full border border-white/20 bg-white/10 px-5 py-3 text-sm text-white placeholder:text-slate-400 focus:border-teal focus:outline-none"
          {...register("email")}
        />
        {errors.email && <p id="newsletter-email-error" role="alert" className="mt-2 text-xs text-red-300">{errors.email.message}</p>}
      </div>
      <button
        type="submit"
        disabled={isSubmitting}
        data-testid="newsletter-submit-btn"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-teal hover:bg-teal-dark disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 transition-colors"
      >
        <Mail size={15} aria-hidden="true" />
        {isSubmitting ? "Subscribing…" : "Subscribe"}
      </button>
    </form>
  );
}

export default function Insights() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const filtered = useMemo(() => {
    return ARTICLES.filter((a) => {
      const matchesCat = category === "All" || a.category === category;
      const q = query.trim().toLowerCase();
      const matchesQuery = !q || a.title.toLowerCase().includes(q) || a.excerpt.toLowerCase().includes(q);
      return matchesCat && matchesQuery;
    });
  }, [query, category]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <SEO
        title="Insights"
        description="Practical perspectives on information security, ISO standards, business continuity, cybersecurity, IT governance, risk and compliance from Numeric Consulting & Solutions."
        path="/insights"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Insights", path: "/insights" }]}
      />
      <PageHero
        eyebrow="Insights"
        title="Perspectives on Security, Standards and Resilience"
        description="Practical writing for executives and practitioners — drawn from real consulting work across management systems, security assessment and operational resilience."
        breadcrumb="Insights"
      />

      <section className="bg-white py-16 sm:py-20" data-testid="insights-explorer">
        <div className="container-site">
          <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
            <div className="relative w-full lg:max-w-sm">
              <label htmlFor="insight-search" className="sr-only">Search insights</label>
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" aria-hidden="true" />
              <input
                id="insight-search"
                type="search"
                placeholder="Search topics…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="insights-search-input"
                className="w-full rounded-full border border-slate-300 bg-white pl-11 pr-4 py-2.5 text-sm text-ink placeholder:text-muted focus:border-corp focus:outline-none"
              />
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by category">
              {["All", ...INSIGHT_CATEGORIES].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategory(c)}
                  aria-pressed={category === c}
                  data-testid={`insights-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`rounded-full px-4 py-2 text-xs sm:text-sm font-semibold border transition-colors ${
                    category === c
                      ? "bg-navy text-white border-navy"
                      : "bg-mist text-ink border-slate-200 hover:border-corp/40"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="mt-14 rounded-2xl border border-dashed border-slate-300 bg-mist p-12 text-center" data-testid="insights-empty-state">
              <p className="font-heading text-xl font-semibold text-navy">No insights match your search</p>
              <p className="mt-2 text-sm text-muted max-w-md mx-auto">
                Try a different search term or category — or subscribe below to be notified when new
                articles are published.
              </p>
              <button
                type="button"
                onClick={() => { setQuery(""); setCategory("All"); }}
                data-testid="insights-reset-filters-btn"
                className="mt-6 rounded-full border border-corp text-corp text-sm font-semibold px-6 py-2.5 hover:bg-corp hover:text-white transition-colors"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <>
              {featured && (
                <Reveal className="mt-12">
                  <Link
                    to={`/insights/${featured.slug}`}
                    data-testid="insights-featured"
                    className="group grid lg:grid-cols-2 overflow-hidden rounded-2xl bg-navy hover:shadow-xl transition-shadow duration-300"
                  >
                    <div className="relative p-8 sm:p-12">
                      <div className="absolute inset-0 grid-pattern-dark" aria-hidden="true" />
                      <div className="relative">
                        <span className="rounded-full bg-teal/15 border border-teal/30 text-teal text-xs font-semibold px-3 py-1">Featured</span>
                        <h2 className="mt-6 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white group-hover:text-teal transition-colors">{featured.title}</h2>
                        <p className="mt-4 text-base leading-relaxed text-slate-300">{featured.excerpt}</p>
                        <div className="mt-6 flex items-center gap-5 text-xs text-slate-400">
                          <span className="font-mono uppercase tracking-[0.2em] text-teal">{featured.category}</span>
                          <span className="inline-flex items-center gap-1.5"><CalendarDays size={12} aria-hidden="true" />{featured.date}</span>
                          <span className="inline-flex items-center gap-1.5"><Clock size={12} aria-hidden="true" />{featured.readTime}</span>
                        </div>
                        <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white">
                          Read article <ArrowRight size={15} aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>
                    <div className="relative min-h-56" aria-hidden="true">
                      <img src={`/covers/${featured.slug}.png`} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    </div>
                  </Link>
                </Reveal>
              )}
              <div className="mt-8 grid md:grid-cols-2 gap-6">
                {rest.map((a, i) => (
                  <Reveal key={a.slug} delay={i * 0.08}>
                    <Link
                      to={`/insights/${a.slug}`}
                      data-testid={`insight-card-${a.slug}`}
                      className="group block h-full overflow-hidden rounded-2xl border border-slate-200 bg-white hover:shadow-md hover:border-corp/25 transition-[box-shadow,border-color] duration-300"
                    >
                      <div className="aspect-[1200/630] overflow-hidden">
                        <img src={`/covers/${a.slug}.png`} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <div className="p-8">
                      <span className="font-mono text-xs uppercase tracking-[0.18em] text-corp">{a.category}</span>
                      <h3 className="mt-4 font-heading text-xl font-semibold text-ink group-hover:text-corp transition-colors">{a.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-muted">{a.excerpt}</p>
                      <div className="mt-5 flex items-center gap-4 text-xs text-muted">
                        <span className="inline-flex items-center gap-1.5"><CalendarDays size={12} aria-hidden="true" />{a.date}</span>
                        <span className="inline-flex items-center gap-1.5"><Clock size={12} aria-hidden="true" />{a.readTime}</span>
                      </div>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-corp">
                        Read article <ArrowRight size={14} aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-1" />
                      </span>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="relative bg-gradient-to-br from-navy via-navy-800 to-navy-900 py-16 sm:py-20 overflow-hidden" data-testid="newsletter-section">
        <div className="absolute inset-0 grid-pattern-dark" aria-hidden="true" />
        <div className="container-site relative">
          <Reveal className="max-w-2xl">
            <p className="eyebrow-light mb-4">Stay Informed</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">
              Be Notified When New Insights Are Published
            </h2>
            <p className="mt-4 text-base text-slate-300 mb-8">
              Leave your work email and we will let you know when new articles go live. No spam — you can ask
              to be removed at any time.
            </p>
            <NewsletterForm />
          </Reveal>
        </div>
      </section>

      <CTASection
        title="Have a Question Our Insights Should Answer?"
        description="Send us the topics and challenges you want covered — or speak directly with our consulting team."
      />
    </>
  );
}
